import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { checkFormStructure, describeFormCondition, type FormCondition } from '@kaba/shared';
import type { Def } from './model';

/** Regeln: alle Bedingungen als Sätze an einem Ort, dazu die Strukturprüfung (R01, R03) */
export function RulesTab({ def, onSelect }: { def: Def; onSelect: (id: string) => void }) {
  const issues = checkFormStructure(def);
  const rows: { id: string; what: string; kind: string; c: FormCondition }[] = [];
  for (const p of def.pages) {
    if (p.visibleIf?.items.length) rows.push({ id: p.id, what: `Seite „${p.title}“`, kind: 'anzeigen, wenn', c: p.visibleIf });
    for (const it of p.items) {
      const qs = it.kind === 'section' ? it.items : [it];
      if (it.kind === 'section' && it.visibleIf?.items.length) rows.push({ id: it.id, what: `Abschnitt „${it.title}“`, kind: 'anzeigen, wenn', c: it.visibleIf });
      for (const q of qs) {
        if (q.visibleIf?.items.length) rows.push({ id: q.id, what: `„${q.label}“`, kind: 'anzeigen, wenn', c: q.visibleIf });
        if (q.requiredIf?.items.length) rows.push({ id: q.id, what: `„${q.label}“`, kind: 'Pflicht, wenn', c: q.requiredIf });
        for (const o of q.options ?? []) if (o.visibleIf?.items.length) rows.push({ id: q.id, what: `Antwort „${o.label}“ bei „${q.label}“`, kind: 'anbieten, wenn', c: o.visibleIf });
      }
    }
  }
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_22rem]">
      <section aria-label="Bedingungen" className="flex flex-col gap-2">
        <h3 className="text-sm font-semibold">Bedingungen im Formular</h3>
        {!rows.length && <p className="rounded-lg border border-dashed border-[var(--line)] p-4 text-sm text-[var(--ink-soft)]">Noch keine Bedingungen. Ein einfaches Formular braucht keine – Bedingungen legst du bei einer Frage unter „Erweitert“ an.</p>}
        <ul className="flex flex-col gap-2">
          {rows.map((r, i) => (
            <li key={i}>
              <button type="button" onClick={() => onSelect(r.id)} className="flex w-full flex-col gap-0.5 rounded-lg border border-[var(--line)] bg-[var(--panel)] p-3 text-left text-sm hover:border-accent">
                <span>
                  <strong>{r.what}</strong> {r.kind}
                </span>
                <span className="text-[var(--ink-soft)]">{describeFormCondition(def, r.c)}</span>
              </button>
            </li>
          ))}
        </ul>
        <p className="text-xs text-[var(--ink-soft)]">
          Regeln beziehen sich nur auf Fragen davor. Verborgene Fragen sind nie Pflicht, ihre Antworten werden entfernt, und alle Vergleiche außer „ist unbeantwortet“ gelten bei fehlender Antwort als nicht erfüllt.
        </p>
      </section>
      <aside aria-label="Prüfung" className="flex flex-col gap-2 rounded-xl border border-[var(--line)] bg-[var(--panel)] p-3">
        <h3 className="text-sm font-semibold">Prüfung</h3>
        {issues.length === 0 ? (
          <p className="flex items-center gap-2 text-sm text-emerald-700 dark:text-emerald-300">
            <CheckCircle2 size={16} aria-hidden /> Aufbau und Bedingungen sind in Ordnung.
          </p>
        ) : (
          <ul className="flex flex-col gap-1.5 text-sm">
            {issues.map((i, k) => (
              <li key={k} className="flex gap-2">
                <AlertTriangle size={15} className={i.blocking ? 'mt-0.5 shrink-0 text-rose-600' : 'mt-0.5 shrink-0 text-amber-600'} aria-hidden />
                <span>
                  {i.message}
                  {!i.blocking && <span className="text-[var(--ink-soft)]"> (Hinweis)</span>}
                </span>
              </li>
            ))}
          </ul>
        )}
      </aside>
    </div>
  );
}
